import React, { useEffect, useState } from "react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, FileText, Eye, CheckCircle2, ShieldCheck, Lock } from "lucide-react";
import { PageHeader, Badge, EmptyState } from "@/components/Common";
import { useAuth } from "@/contexts/AuthContext";

const ADMIN_ROLES = ["managing_director", "executive_director", "general_manager"];

export default function Policy() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState({
    title: "", version: "1.0", effective_date: "", notes: "", is_active: true,
  });
  const [file, setFile] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/policies");
        if (!cancelled) setItems(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);
  const reload = () => setRefreshKey((k) => k + 1);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please choose a PDF file"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: up } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/policies", {
        title: form.title, version: form.version,
        effective_date: form.effective_date || null,
        notes: form.notes, is_active: form.is_active,
        file_id: up.id,
      });
      toast.success("Policy uploaded");
      setOpen(false);
      setForm({ title: "", version: "1.0", effective_date: "", notes: "", is_active: true });
      setFile(null);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const activate = async (id) => {
    try {
      await api.post(`/policies/${id}/activate`);
      toast.success("Marked as current policy");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this policy document?")) return;
    try {
      await api.delete(`/policies/${id}`);
      toast.success("Deleted");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const current = items.find((p) => p.is_active);
  const others = items.filter((p) => !p.is_active);

  return (
    <div>
      <PageHeader
        eyebrow="Internal · Staff only"
        title="Operations Policy"
        subtitle="The official operating manual for all staff. Read-only for everyone; only senior leadership can upload, replace or retire policy documents."
        actions={isAdmin ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-900 hover:bg-emerald-950 text-white" data-testid="policy-new-button">
                <Plus size={16} className="mr-1.5" /> Upload policy
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-display">Upload Operations Policy</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="policy-form">
                <div>
                  <Label>Title *</Label>
                  <Input required value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="mt-1" placeholder="Abrah Operations Policy 2026"
                    data-testid="policy-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Version</Label>
                    <Input value={form.version}
                      onChange={(e) => setForm({ ...form, version: e.target.value })}
                      className="mt-1" placeholder="1.0" />
                  </div>
                  <div>
                    <Label>Effective date</Label>
                    <Input type="date" value={form.effective_date}
                      onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                      className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label>Change notes / summary</Label>
                  <Textarea rows={2} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="mt-1" placeholder="What's new in this version" />
                </div>
                <div>
                  <Label>PDF document *</Label>
                  <Input type="file" required accept="application/pdf"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="mt-1" data-testid="policy-file-input" />
                  <div className="text-xs text-gray-500 mt-1">PDF only. Max 25 MB.</div>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                  Mark as the current active policy (deactivates previous version)
                </label>
                <DialogFooter>
                  <Button type="submit" disabled={busy}
                    className="bg-emerald-900 hover:bg-emerald-950 text-white"
                    data-testid="policy-submit-button">
                    {busy ? "Uploading…" : "Upload"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        ) : (
          <div className="text-xs text-gray-500 inline-flex items-center gap-1.5">
            <Lock size={12} /> Upload restricted to leadership
          </div>
        )}
      />

      {/* Current policy hero */}
      {current && (
        <div className="bg-emerald-900 text-white border border-emerald-950 rounded-md p-5 mb-6">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-md bg-amber-500 text-emerald-950 flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={28} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-semibold mb-1">Current operations policy</div>
              <h2 className="font-display text-2xl font-bold leading-tight">{current.title}</h2>
              <div className="text-xs text-emerald-100/80 mt-1">
                Version {current.version} · Effective {current.effective_date || (current.created_at || "").slice(0, 10)} · Uploaded by {current.uploaded_by_name}
              </div>
              {current.notes && <p className="text-sm text-emerald-50/90 mt-2 line-clamp-2">{current.notes}</p>}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button onClick={() => setViewing(current)} data-testid="policy-view-current"
                className="bg-amber-500 hover:bg-amber-400 text-emerald-950 font-semibold">
                <Eye size={14} className="mr-1.5" /> Read policy
              </Button>
            </div>
          </div>
        </div>
      )}

      {!current && !loading && items.length === 0 && (
        <EmptyState
          title="No policy document uploaded yet"
          hint={isAdmin
            ? "Click “Upload policy” to publish your operations manual to all staff."
            : "Your leadership hasn't uploaded the operations policy yet. Check back soon."} />
      )}

      {/* Previous versions */}
      {others.length > 0 && (
        <div>
          <h3 className="font-display font-semibold text-gray-900 mb-3">Previous versions</h3>
          <ul className="divide-y divide-gray-100 bg-white border border-gray-200 rounded-md" data-testid="policy-versions">
            {others.map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-4 py-3">
                <FileText size={16} className="text-emerald-700" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">{p.title}</div>
                  <div className="text-xs text-gray-500">
                    v{p.version} · {(p.effective_date || p.created_at || "").slice(0, 10)} · {p.uploaded_by_name}
                  </div>
                </div>
                <Badge tone="gray">Archived</Badge>
                <Button variant="ghost" size="sm" onClick={() => setViewing(p)}
                  data-testid={`policy-view-${p.id}`}>
                  <Eye size={13} />
                </Button>
                {isAdmin && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => activate(p.id)}
                      className="text-emerald-800"
                      data-testid={`policy-activate-${p.id}`}>
                      <CheckCircle2 size={13} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(p.id)}
                      className="text-red-700 hover:bg-red-50"
                      data-testid={`policy-delete-${p.id}`}>
                      <Trash2 size={13} />
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {viewing && (
        <Dialog open onOpenChange={(v) => !v && setViewing(null)}>
          <DialogContent className="max-w-4xl h-[88vh] p-0 overflow-hidden flex flex-col">
            <DialogHeader className="px-5 py-3 border-b border-gray-200 bg-gray-50">
              <DialogTitle className="font-display flex items-center justify-between">
                <span>{viewing.title}</span>
                <div className="flex items-center gap-2 text-xs font-normal text-gray-600">
                  <Badge tone="amber">Read-only</Badge>
                  <span>v{viewing.version}</span>
                </div>
              </DialogTitle>
            </DialogHeader>
            <iframe
              title={viewing.title}
              src={fileUrl(viewing.file_id) + "#toolbar=1&navpanes=0"}
              className="flex-1 w-full bg-gray-100"
              data-testid="policy-pdf-viewer"
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
